import { createRef } from 'react';
import {IContentBlock, IDesignBlock} from '../../types/iblock';
import { IDividerBlockData } from '../../types/content-block-data';
import pageBuilderStore from '../../stores/pageBuilderStore';
import blocksManager from '../../stores/blocks-manager';
import contentManager from '../../stores/content-manager';

/**
 * Converts a DividerBlock (IContentBlock) to an HTML string.
 * Renders as a <hr>.
 * 
 * @param block - The IContentBlock (should be a blockType "divider").
 * @returns HTML string (e.g., <hr />)
 * 
 * @remarks
 * - Added a new local variable `isEmail` for toggling serialization markup.
 */
export function fromDividerBlockToHTML (block: IContentBlock): string {
	if (block.blockType !== "divider") return "";

  const isEmail = pageBuilderStore.getIsEmail();
  const data = block.blockData as IDividerBlockData;
  const selector = block.selector || "d2d-divider";

  let dividerStyle = `width: ${data.width?.value ? `${data.width.value}${data.width.unit || "%"}` : "100%"}; `
      dividerStyle += `height: ${data.height?.value ? `${data.height.value}${data.height.unit || "px"}` : "1px"}; `
      dividerStyle += `background-color: ${data.color || "#CCCCCC"}; `;
      dividerStyle += `padding: ${blocksManager.dimensionToCss(data.padding) || "0px"}; `;
      dividerStyle += isEmail && `margin: ${blocksManager.dimensionToCss(data.margins) || "0px"}; `;
      dividerStyle += `border: none; display: block;`;

	return isEmail ? `<tr><td style="padding: ${blocksManager.dimensionToCss(data.padding) || "0px"};"><hr class="${selector}" style="${dividerStyle}" /></td></tr>` : `<hr class="${selector}" style="${dividerStyle}" />`;
}

/**
 * Serializes a DividerBlock's data for storage or export.
 * Omits UI-only fields.
 *
 * @param block - The IContentBlock (should be blockType "divider")
 * @returns A JSON-serializable object
 */
export function fromDividerBlockToJSON(block: IContentBlock): object {
  if (block.blockType !== "divider") return {};
  // No extra data fields, but preserve the structure for consistency
  return {
    contentBlockId: block.contentBlockId,
    blockType: block.blockType,
    selector: block.selector,
    blockData: {},
  };
}

/**
 * Reconstructs a DividerBlock from JSON data.
 *
 * @param json - The JSON object to parse
 * @returns An IContentBlock instance (for use in our store/UI)
 */
export function fromJSONToDividerBlock(json: any, context: "canvas" | "columns" = "canvas"): IDesignBlock {
  const dividerStyles = contentManager.getStylesForSelector(json.selector);
  
  const newDesignBlockRef = createRef<HTMLDivElement>();
	const newContentBlockRef = createRef<HTMLElement>(); // generic for now
  
  const block:IDesignBlock = {
    designBlockId: crypto.randomUUID(), // always generate fresh
    designBlockRef: newDesignBlockRef,
    contentBlock: {
      contentBlockId: json.contentBlockId,
      blockType: json.blockType,
      contentBlockRef: newContentBlockRef,
      selector: json.selector,
      blockData: {
        width: blocksManager.parseDimensions(dividerStyles?.["width"], "single"),
        height: blocksManager.parseDimensions(dividerStyles?.["height"], "single"),
        color: dividerStyles?.["background-color"],
        padding: blocksManager.parseDimensions(dividerStyles?.["padding"], "four-side"),
        margins: blocksManager.parseDimensions(dividerStyles?.["margin"], "four-side"),
        alignment: dividerStyles?.["alignment"]
      } as IDividerBlockData,
    } as IContentBlock,
  };

  blocksManager.addDesignTreeEntry({
    contentBlockId: block.contentBlock.contentBlockId,
    designBlockId: block.designBlockId,
    designBlockRef: newDesignBlockRef,
    contentBlockRef: newContentBlockRef,
    contentBlockType: block.contentBlock.blockType.toUpperCase(),
    location: context,
  });

  return block;
}